import { queryOptions } from '@w/react-query/db'
import type { Tel } from '@w/tel'
import type { ReactNode } from 'react'
import type { PageRef } from '@w/content-blocks/schema'
import { useQuery, type UseQueryOptions } from '@w/react-query'
import type { SpaceResource, SpacesResources, SpacesView } from '../router/spaces-resources'
import { compareSearchHits, pageSearchOptions, preparePageSearch, type PageSearchEmbeddings, type PageSearchMode, type PageSearchResult } from '../search/page-search'
import { pageBacklinksOptions } from './page-backlinks'
import { pageParentColumn, pageMetaColumns, pageMetaSchema, pageReadColumns, pageReadSchema, type PageRow } from './page-read'
import { navSkippedKinds, navStopKinds } from './schema'
import { usablePage } from './page-eligibility'
import { isUnreadable, itemRowsOptions, readable, readableRowsOptions } from '../dead-letter-queue/item-readers'
export { usablePage } from './page-eligibility'

export function contentResources(view: SpacesView) {
  return view.resources
}

/** The tree read has no per-call input: built once per workspace db (as Drizzle/Kysely prepare a query once),
 * not on every render that asks for its options. */
const spaceTreeQueries = new WeakMap<SpaceResource['db'], ReturnType<SpaceResource['db']['query']>>()

function spaceTreeQuery(resource: SpaceResource) {
  const { db, space } = resource
  const query = spaceTreeQueries.get(db) ?? spaceTreeSql(db)
  spaceTreeQueries.set(db, query)
  return { query, schema: pageMetaSchema(space.id) }
}

function spaceTreeSql(db: SpaceResource['db']) {
  const pages = db.pages
  return db.query`SELECT ${pageMetaColumns(pages)} FROM ${pages}
    WHERE ${pages.kind.ne('databaseRow')} AND NOT ${pages.kind.inArray(navSkippedKinds())} AND ${pages.deletedAt.isNull()} AND ${usablePage(pages)}
      AND NOT EXISTS (SELECT 1 FROM ${pages} parent
        WHERE parent.id = ${pageParentColumn(pages)} AND parent.kind IN (SELECT value FROM json_each(${JSON.stringify(navStopKinds())}))) ORDER BY ${pages.id}`
}

/** One tree read: healthy rows, with the rows it could not decode carried as `unreadable` placeholders. */
export function spaceTreeOptions(resource: SpaceResource) {
  const { query, schema } = spaceTreeQuery(resource)
  const read = itemRowsOptions(resource, query, schema, 'page', 'PAGE_METADATA').queryFn
  return { ...queryOptions(query, schema, { staleTime: Infinity }), queryFn: async (qctx?: { tel?: Tel }) => {
    const items = await read(qctx)
    return Object.assign(readable(items), { unreadable: items.filter(item => isUnreadable(item)) })
  } }
}

export function pageSourceOptions(resource: SpaceResource, ref: PageRef) {
  const { db } = resource
  const query = db.query`SELECT ${pageReadColumns(db.pages)} FROM ${db.pages}
    WHERE ${db.pages.id.eq(ref.pageId)} AND ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)}`
  const schema = pageReadSchema(ref.spaceId, undefined, resource.userId)
  return { ...queryOptions(query, schema, { staleTime: Infinity }), queryFn: readableRowsOptions(resource, query, schema, 'page', 'PAGE_DOCUMENT').queryFn }
}

export function resolvePageSource(spaces: SpacesResources, workspaceId: string, ref: PageRef): Promise<PageRow | undefined> {
  const resource = contentResources(spaces.snapshot(workspaceId)).find(value => value.space.id === ref.spaceId)
  return resource ? resource.queryClient.fetchQuery(pageSourceOptions(resource, ref)).then(rows => rows[0]) : Promise.resolve(resource)
}

export function spaceBacklinksOptions(resource: SpaceResource, target: PageRef) {
  return pageBacklinksOptions(resource.db, resource.space.id, target)
}

export type QualifiedSearchHit = PageSearchResult & PageRef
export function mergeSearchHits(hits: QualifiedSearchHit[], query: string, limit: number) {
  const unique = new Map<string, QualifiedSearchHit>()
  for (const hit of hits) {
    const key = JSON.stringify([hit.spaceId, hit.pageId])
    const previous = unique.get(key)
    if (!previous || hit.score > previous.score) unique.set(key, hit)
  }
  return [...unique.values()].toSorted((left, right) => compareSearchHits(left, right, query)).slice(0, limit)
}

/** Embed once before creating any physical-file query; fallback mode belongs to the result. */
export async function prepareSpaceSearch(input: { query: string; embeddings: PageSearchEmbeddings; mode?: PageSearchMode; limit?: number; tel: Tel }) {
  const stages = await preparePageSearch(input, prepared => ({ mode: prepared.mode, query: prepared.query, limit: prepared.limit,
    options(this: void, resource: SpaceResource) {
      const options = pageSearchOptions({ db: resource.db, spaceId: resource.space.id, ...prepared })
      return { ...options, queryFn: (qctx?: { tel?: Tel }) => options.queryFn(qctx).then(hits => hits.map(hit => Object.assign({}, hit, { spaceId: resource.space.id }))) }
    },
  }))
  return { ...stages.local, reranked: stages.reranked }
}

/** Each recursive component owns one library hook. Membership changes alter the component tree. */
export function SpaceQueries<T>({ resources, options, children }: {
  resources: SpaceResource[]
  options(this: void, resource: SpaceResource): UseQueryOptions<T>
  children(this: void, results: { resource: SpaceResource; data: T | undefined; error: Error | undefined }[]): ReactNode
}) {
  const [first, ...rest] = resources
  if (!first) return children([])
  return <SpaceQuery key={`${first.userId}:${first.space.id}`} resource={first} options={options(first)}>
    {result => <SpaceQueries resources={rest} options={options}>{results => children([result, ...results])}</SpaceQueries>}
  </SpaceQuery>
}

function SpaceQuery<T>({ resource, options, children }: {
  resource: SpaceResource
  options: UseQueryOptions<T>
  children(this: void, result: { resource: SpaceResource; data: T | undefined; error: Error | undefined }): ReactNode
}) {
  const result = useQuery(options, resource.queryClient)
  return children({ resource, data: result.data, error: result.error })
}

export function useSpacesView(spaces: SpacesResources, workspaceId: string, directoryClient: SpaceResource['queryClient']) {
  return useQuery(spaces.queryOptions(workspaceId), directoryClient)
}
